import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Upload, User, Save, Users as UsersIcon, Download } from 'lucide-react';
import { toast } from 'sonner';
import PinchZoomPan from '../components/PinchZoomPan';

const NodeAvatar = ({ node, onClick, size = 90 }) => {
  const hasName = node?.nom || node?.prenom;
  const fullName = hasName ? `${node.prenom || ''} ${node.nom || ''}`.trim() : '';
  const isSmall = size <= 60;
  return (
    <div className="flex flex-col items-center gap-1.5 cursor-pointer group w-full" onClick={onClick}>
      <div
        style={{ width: size, height: size }}
        className="rounded-full border-4 border-purple-200 group-hover:border-purple-500 transition-all overflow-hidden bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center shadow-md group-hover:shadow-xl flex-shrink-0"
      >
        {node?.photo ? (
          <img src={node.photo} alt={fullName} className="w-full h-full object-cover" />
        ) : (
          <User className={isSmall ? 'h-7 w-7 text-purple-400' : 'h-10 w-10 text-purple-400'} />
        )}
      </div>
      <div className="text-center w-full px-1">
        <p className={`${isSmall ? 'text-[9px]' : 'text-xs'} font-bold text-gray-900 uppercase tracking-wide break-words leading-tight`}>
          {fullName || '— À renseigner —'}
        </p>
        <p className={`${isSmall ? 'text-[10px]' : 'text-xs'} text-purple-600 font-medium mt-0.5 break-words leading-tight`}>
          {node?.titre || ''}
        </p>
      </div>
    </div>
  );
};

// Flèche verticale simple
const VLine = ({ h = 20 }) => <div style={{ height: h }} className="w-0.5 bg-purple-300 mx-auto" />;

export default function Organigramme({ user, onLogout }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [nodes, setNodes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // node en cours d'édition
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    axios
      .get(`${API}/api/organigramme`)
      .then((r) => setNodes(r.data?.nodes || {}))
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [API]);

  const openEdit = (nodeId) => {
    if (!isAdmin) {
      toast.info('Seul un administrateur peut modifier');
      return;
    }
    setEditing({ ...nodes[nodeId], id: nodeId });
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image trop lourde (max 2 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setEditing((d) => ({ ...d, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/organigramme/node/${editing.id}`, {
        titre: editing.titre,
        nom: editing.nom,
        prenom: editing.prenom,
        photo: editing.photo,
      });
      setNodes((prev) => ({ ...prev, [editing.id]: res.data.node }));
      toast.success('Poste mis à jour');
      setEditing(null);
    } catch (e) {
      console.error(e);
      toast.error("Erreur à l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/organigramme/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `organigramme-sesam-${ts}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('PDF téléchargé');
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading || !nodes) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="organigramme-page">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={() => navigate(-1)} variant="outline" className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <UsersIcon className="h-7 w-7 text-purple-600" /> Organigramme
            </h1>
            <p className="text-gray-600 text-sm">
              {isAdmin ? 'Cliquez sur un poste pour modifier nom, prénom et photo.' : 'Structure hiérarchique de l\'entreprise.'}
            </p>
          </div>
          <Button
            onClick={exportPdf}
            disabled={exporting}
            className="gap-2 bg-[#0B1E42] hover:bg-[#1e3a8a] text-white"
            data-testid="btn-export-organigramme-pdf"
          >
            <Download className="h-4 w-4" /> {exporting ? 'Génération…' : 'Exporter en PDF'}
          </Button>
        </div>

        {/* Organigramme */}
        <Card className="bg-gradient-to-b from-white to-purple-50/30 shadow-lg">
          <CardContent className="p-3 sm:p-6">
            <p className="text-xs text-gray-500 mb-3 sm:hidden">
              💡 Pincez pour zoomer, glissez pour déplacer. Boutons disponibles en bas à droite.
            </p>
            <PinchZoomPan className="rounded-md border border-purple-100 bg-white/60 h-[65vh] sm:h-auto sm:min-h-[600px]" >
              <div className="min-w-[1100px] max-w-[1400px] mx-auto p-6">
              {/* Niveau 1 : Directeur */}
              <div className="flex justify-center">
                <NodeAvatar node={nodes.directeur} onClick={() => openEdit('directeur')} size={110} />
              </div>

              <VLine h={18} />
              {/* Ligne horizontale reliant les 3 postes niveau 2 */}
              <div className="relative h-0.5 bg-purple-300 mx-auto" style={{ width: '70%' }} />

              {/* Niveau 2 : Associé 1 - Associé 2 - Responsable */}
              <div className="grid grid-cols-3 gap-8 mt-0 relative">
                <div className="flex flex-col items-center">
                  <VLine h={18} />
                  <NodeAvatar node={nodes.associe_1} onClick={() => openEdit('associe_1')} size={92} />
                </div>
                <div className="flex flex-col items-center">
                  <VLine h={18} />
                  <NodeAvatar node={nodes.associe_2} onClick={() => openEdit('associe_2')} size={92} />
                </div>
                <div className="flex flex-col items-center">
                  <VLine h={18} />
                  <NodeAvatar node={nodes.responsable} onClick={() => openEdit('responsable')} size={92} />
                </div>
              </div>

              {/* Niveau 3 - descendants associé_1 (colonne gauche) ET responsable (étendu sous Charles MATHEY + Amadou) */}
              <div className="grid grid-cols-3 gap-8 mt-4">
                {/* Colonne Associé 1 → 3 investisseurs */}
                <div className="flex flex-col items-center">
                  <VLine h={14} />
                  <div className="relative h-0.5 bg-purple-300" style={{ width: '86%' }} />
                  <div className="grid grid-cols-3 gap-2 w-full mt-0">
                    {['investisseur_1', 'investisseur_2', 'investisseur_3'].map((id) => (
                      <div key={id} className="flex flex-col items-center min-w-0">
                        <VLine h={14} />
                        <NodeAvatar node={nodes[id]} onClick={() => openEdit(id)} size={60} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Colonnes 2+3 fusionnées : 7 postes étalés au milieu entre Charles MATHEY et Amadou.
                    La ligne verticale part directement de Responsable (Amadou) en colonne 3 = 75% du conteneur col-span-2. */}
                <div className="col-span-2 flex flex-col items-center relative">
                  <div className="w-full flex" style={{ paddingLeft: '75%' }}>
                    <div className="-translate-x-1/2"><VLine h={14} /></div>
                  </div>
                  <div className="relative h-0.5 bg-purple-300" style={{ width: '96%' }} />
                  <div className="grid grid-cols-7 gap-2 w-full mt-0">
                    {['commercial_1', 'commercial_2', 'technicien', 'responsable_com', 'chef_agence', 'graphiste', 'designer'].map((id) => (
                      <div key={id} className="flex flex-col items-center min-w-0">
                        <VLine h={14} />
                        <NodeAvatar node={nodes[id]} onClick={() => openEdit(id)} size={48} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              </div>
            </PinchZoomPan>
          </CardContent>
        </Card>

        {/* Modal édition */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && !saving && setEditing(null)}>
          <DialogContent className="max-w-md" data-testid="edit-node-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-purple-600" />
                Modifier : {editing?.titre}
              </DialogTitle>
            </DialogHeader>
            {editing && (
              <div className="space-y-4 py-2">
                {/* Photo */}
                <div className="flex flex-col items-center gap-3">
                  <div className="w-32 h-32 rounded-full border-4 border-purple-200 overflow-hidden bg-purple-100 flex items-center justify-center">
                    {editing.photo ? (
                      <img src={editing.photo} alt="Aperçu" className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-16 w-16 text-purple-400" />
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFile}
                    data-testid="photo-upload-input"
                  />
                  <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2">
                    <Upload className="h-4 w-4" /> Choisir une photo
                  </Button>
                  {editing.photo && (
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, photo: '' })}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Retirer la photo
                    </button>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Titre du poste</Label>
                  <Input value={editing.titre || ''}
                         onChange={(e) => setEditing({ ...editing, titre: e.target.value })}
                         data-testid="edit-titre" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Prénom</Label>
                    <Input value={editing.prenom || ''}
                           onChange={(e) => setEditing({ ...editing, prenom: e.target.value })}
                           data-testid="edit-prenom" />
                  </div>
                  <div>
                    <Label className="text-xs">Nom</Label>
                    <Input value={editing.nom || ''}
                           onChange={(e) => setEditing({ ...editing, nom: e.target.value })}
                           data-testid="edit-nom" />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Annuler</Button>
              <Button onClick={saveEdit} disabled={saving}
                      className="bg-purple-600 hover:bg-purple-700 gap-2"
                      data-testid="save-node-btn">
                <Save className="h-4 w-4" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
